import { FC, useEffect, useRef, useState } from "react";
import Icon from "components/Elements/Icon/Icon";
import { useContent } from "contexts/contentContext/contentContext";
import CustomPopup from "../Popups/CustomPopup";
import Button from "components/Elements/Button/Button";
import { actionStatuses, getMailStatuses, mailStatuses } from "../helpers";
import useOnClickOutside from "hooks/useOnClickOutside";
import { pickerStatusKeys } from "../Picker/helpers";
import useInbox from "hooks/useInbox.ts";
import { useAppSelector } from "../../../../store/hooks";
import {
  selectFParam,
  selectPickerSearchParams,
  selectSearchParams,
  selectTParam,
} from "../../../../store/selectors/inboxSelectors";
import type { MailStatusT } from "../../../../store/slices/inboxSlice";

type StatusFilterT = {
  asIcon?: boolean;
};

const StatusFilter: FC<StatusFilterT> = (props) => {
  const { asIcon } = props;
  const {
    setSelectedMail,
    getIsFetching,
    setSearchParams,
    setPageNumber,
    setPickerSearchParams,
  } = useInbox();
  const searchParams = useAppSelector(selectSearchParams);
  const pickerSearchParams = useAppSelector(selectPickerSearchParams);
  const pageType = useAppSelector(selectTParam);
  const pageCategory = useAppSelector(selectFParam);
  const { getTextByKey } = useContent();
  const [selectedMailStatus, setSelectedMailStatuses] = useState<MailStatusT[]>(
    []
  );
  const [selectedPickerStatus, setSelectedPickerStatuses] = useState<string[]>(
    []
  );
  const [selectedActionStatus, setSelectedActionStatuses] = useState<string[]>(
    []
  );
  const [showDropdown, setShowDropdown] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!showDropdown) {
      setSelectedMailStatuses(searchParams?.sm || []);
      setSelectedActionStatuses(searchParams?.sa || []);
      setPickerSearchParams({
        ps: pickerSearchParams?.ps || [],
      });
    }
  }, [showDropdown, getIsFetching()]);

  useEffect(() => {
    // clear local state (not saved checked items)
    // check if dropdown closed and then rewrite local state by data from URL
    if (pageType !== "picker") {
      setSelectedMailStatuses(searchParams?.sm || []);
      setSelectedActionStatuses(searchParams?.sa || []);
    } else {
      setSelectedPickerStatuses(pickerSearchParams?.ps || []);
    }
  }, [showDropdown]);

  useOnClickOutside(ref, () => setShowDropdown(false));

  const toggleShowDropdown = () => {
    setShowDropdown((prev) => !prev);
  };

  const handleSelectMailStatus = (status: MailStatusT) => {
    const exists = selectedMailStatus.includes(status);
    if (exists) {
      setSelectedMailStatuses((prev) =>
        prev.filter((value) => value !== status)
      );
    } else {
      setSelectedMailStatuses((prev) => [...prev, status]);
    }
  };

  const handleSelectActionStatus = (status: string) => {
    setSelectedActionStatuses((prev) =>
      prev.includes(status) ? [] : [status]
    );
  };

  const handleSelectPickerStatus = (status: string) => {
    const exists = selectedPickerStatus.includes(status);
    if (exists) {
      setSelectedPickerStatuses((prev) =>
        prev.filter((value) => value !== status)
      );
    } else {
      setSelectedPickerStatuses((prev) => [...prev, status]);
    }
  };

  const handleClearAll = () => {
    setSelectedMail(null);

    if (pageType === "picker") {
      setSelectedPickerStatuses([]);
    } else {
      setSelectedActionStatuses([]);
      setSelectedMailStatuses([]);
      setSearchParams({
        sm: [],
        sa: [],
      });
    }
    setPageNumber(1);
    setShowDropdown(false);
  };

  const handleApply = () => {
    setSelectedMail(null);

    if (pageType === "picker") {
      setPickerSearchParams({
        ps: selectedPickerStatus,
      });
    } else {
      setSearchParams({
        sm: selectedMailStatus,
        sa: selectedActionStatus,
      });
    }
    setPageNumber(1);
    setShowDropdown(false);
  };

  const selectedCount = () =>
    pageType === "picker"
      ? pickerSearchParams.ps?.length
      : searchParams.sm?.length || searchParams.sa?.length;

  return (
    <div
      className={`filter-status-wrapper ${
        asIcon ? "filter-status-wrapper-as-icon" : ""
      }`}
      ref={ref}
    >
      <div
        className={`filter-status-button ${
          !!selectedCount() ? "blue-border" : ""
        }`}
        onClick={toggleShowDropdown}
      >
        {!!selectedCount() && <div className="counter">{selectedCount()}</div>}
        <Icon name="newInbox/filter" darkGray large />
        {!asIcon && <span>{getTextByKey("filter-status")}</span>}
      </div>
      <div className="custom-popup-wrapper">
        <CustomPopup showState={showDropdown} topPosition={50}>
          <div className="checkbox-list-dropdown">
            <Icon
              name="x"
              large
              gray
              onClick={() => setShowDropdown(false)}
              iconClass="close-icon"
            />
            <h1>{getTextByKey("filter-title")}</h1>
            <div className="filter-status-scroll-constraint">
              <hr />
              {pageType !== "picker" && (
                <h2>{getTextByKey("filter-title-subtitle-mail")}</h2>
              )}
              {pageType === "picker" ? (
                <ul>
                  {" "}
                  {pickerStatusKeys
                    .filter((key) => key !== "new" && key !== "deleted")
                    .map((status) => (
                      <li
                        key={status}
                        onClick={() => handleSelectPickerStatus(status)}
                      >
                        <div
                          className={`checkbox-wrapper ${
                            selectedPickerStatus.includes(status)
                              ? "selected"
                              : ""
                          }`}
                        >
                          {selectedPickerStatus.includes(status) && (
                            <Icon
                              name="newInbox/mark"
                              iconClass="mark-icon"
                              blue
                            />
                          )}
                        </div>
                        {getTextByKey(`${status}-picker-status`)}
                      </li>
                    ))}
                </ul>
              ) : (
                <ul>
                  {getMailStatuses(pageType, pageCategory)?.map((status) => {
                    const mailStatusKey = (mailStatuses as any)[status].name;
                    return (
                      <li
                        key={status}
                        onClick={() =>
                          handleSelectMailStatus(status as MailStatusT)
                        }
                      >
                        <div
                          className={`checkbox-wrapper ${
                            selectedMailStatus.includes(status as MailStatusT)
                              ? "selected"
                              : ""
                          }`}
                        >
                          {selectedMailStatus.includes(
                            status as MailStatusT
                          ) && (
                            <Icon
                              name="newInbox/mark"
                              iconClass="mark-icon"
                              blue
                            />
                          )}
                        </div>
                        {getTextByKey(mailStatusKey)}
                      </li>
                    );
                  })}
                </ul>
              )}
              {pageCategory !== "jmail" &&
                pageType !== "picker" &&
                pageType !== "trash" && (
                  <>
                    <hr />
                    <h2>{getTextByKey("filter-title-subtitle-actions")}</h2>
                    <ul>
                      {Object.keys(
                        actionStatuses[
                          (pageCategory as keyof typeof actionStatuses) ||
                            "default"
                        ]
                      )?.map((status) => (
                        <li
                          key={status}
                          onClick={() => handleSelectActionStatus(status)}
                        >
                          <div
                            className={`checkbox-wrapper ${
                              selectedActionStatus.includes(status)
                                ? "selected"
                                : ""
                            }`}
                          >
                            {selectedActionStatus.includes(status) && (
                              <Icon
                                name="newInbox/mark"
                                iconClass="mark-icon"
                                blue
                              />
                            )}
                          </div>
                          {getTextByKey(`${status}-action-status`)}
                        </li>
                      ))}
                    </ul>
                  </>
                )}
            </div>
            <div className="buttons">
              <Button onClick={handleApply}>
                {getTextByKey("filter-apply")}
              </Button>
              <button className="clear-button" onClick={handleClearAll}>
                {getTextByKey("filter-clear-all")}
              </button>
            </div>
          </div>
        </CustomPopup>
      </div>
    </div>
  );
};

export default StatusFilter;
